import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingAPI } from '../../api/bookings';
import { businessAPI } from '../../api/businesses';
import { useAuth } from '../../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { resolveImageUrl } from '../../utils/formatters';

const CreateBooking = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { user } = useAuth();

  // Redirect business owners from customer booking flow
  useEffect(() => {
    if (user?.role === 'business_owner') {
      toast.error('Business owners cannot make bookings through the customer flow.');
      navigate('/');
    }
  }, [user, navigate]);
  const [businesses, setBusinesses] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotMessage, setSlotMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [selectedSlotDetails, setSelectedSlotDetails] = useState(null);
  const [staffAvatarError, setStaffAvatarError] = useState(false);
  const [serviceImageError, setServiceImageError] = useState(false);
  const [formData, setFormData] = useState({
    business_id: businessId || '',
    service_id: '',
    staff_id: '',
    booking_date: '',
    start_time: '',
    notes: ''
  });

  // Load all active businesses on mount
  useEffect(() => {
    loadBusinesses();
  }, []);

  // Sync business_id with URL parameter - FIXED
  useEffect(() => {
    if (businessId && businessId !== 'undefined' && businessId !== formData.business_id) {
      setFormData(prev => ({
        ...prev,
        business_id: businessId,
        service_id: '',
        staff_id: '',
        start_time: ''
      }));
    }
  }, [businessId]);

  // Load services and staff when business is selected
  useEffect(() => {
    if (formData.business_id && formData.business_id !== 'undefined' && formData.business_id !== '') {
      loadServices(formData.business_id);
      loadStaff(formData.business_id);
    } else {
      setServices([]);
      setStaff([]);
    }
  }, [formData.business_id]);

  // Load available slots when business, service, and date are selected
  useEffect(() => {
    const isValid = (id) => id && id !== 'undefined' && id !== '';
    if (isValid(formData.business_id) && isValid(formData.service_id) && formData.booking_date) {
      loadAvailableSlots();
    } else {
      setAvailableSlots([]);
      setSelectedSlotDetails(null);
    }
  }, [formData.business_id, formData.service_id, formData.booking_date, formData.staff_id]);

  const loadBusinesses = async () => {
    try {
      setLoadingBusinesses(true);
      const response = await businessAPI.getAll();
      console.log('Businesses loaded:', response.data);
      
      const payload = response?.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.businesses) ? payload.businesses : []);

      if (list) {
        const activeBusinesses = list.filter(b => b.is_active != false && b.is_active != 0);
        setBusinesses(activeBusinesses);
      } else {
        setBusinesses([]);
      }
    } catch (error) {
      console.error('Failed to load businesses:', error);
      toast.error('Failed to load businesses. Please refresh the page.');
      setBusinesses([]);
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const loadServices = async (businessId) => {
    try {
      const response = await businessAPI.getServices(businessId);
      console.log('Services loaded:', response.data);
      
      if (response.data && response.data.data) {
        const activeServices = response.data.data.filter(s => s.is_active != false && s.is_active != 0);
        setServices(activeServices);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error('Failed to load services:', error);
      toast.error('Failed to load services');
      setServices([]);
    }
  };

  const loadStaff = async (businessId) => {
    try {
      const response = await businessAPI.getStaff(businessId);
      console.log('Staff loaded:', response.data);
      
      if (response.data && response.data.data) {
        const availableStaff = response.data.data.filter(s => s.is_available != false && s.is_available != 0);
        setStaff(availableStaff);
      } else {
        setStaff([]);
      }
    } catch (error) {
      console.error('Failed to load staff:', error);
      setStaff([]);
    }
  };

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      console.log('Loading available slots for:', {
        business_id: formData.business_id,
        service_id: formData.service_id,
        staff_id: formData.staff_id,
        date: formData.booking_date
      });
      
      const response = await bookingAPI.getAvailableSlots({
        business_id: formData.business_id,
        service_id: formData.service_id,
        staff_id: formData.staff_id || null,
        date: formData.booking_date
      });
      
      console.log('Available slots response:', response.data);
      
      if (response.data && response.data.data && response.data.data.available_slots) {
        const slots = response.data.data.available_slots.map(slot => ({
          ...slot,
          display_time: slot.display_time_local || slot.display_time || (slot.start_time && slot.end_time ? `${slot.start_time} - ${slot.end_time}` : (slot.start_time || ''))
        }));
        
        setAvailableSlots(slots);
        
        const anyAvailable = slots.some(s => s.available != false && s.available != 0);
        if (!anyAvailable) {
          const message = response.data.data.message || 'No available slots for this date. Please select another date.';
          setSlotMessage(message);
          toast(message, {
            icon: '📅',
            duration: 4000
          });
        } else {
          setSlotMessage('');
        }
      } else {
        setAvailableSlots([]);
        setSlotMessage(response.data?.message || 'No available slots for this date.');
      }
    } catch (error) {
      console.error('Failed to load slots:', error);
      toast.error('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setFormData({ ...formData, start_time: slot.start_time });
    setSelectedSlotDetails({
      start_time: slot.start_time,
      end_time: slot.end_time,
      display_time: slot.display_time
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.business_id) {
      toast.error('Please select a business');
      return;
    }
    if (!formData.service_id) {
      toast.error('Please select a service');
      return;
    }
    if (!formData.booking_date) {
      toast.error('Please select a date');
      return;
    }
    if (!formData.start_time) {
      toast.error('Please select a time slot');
      return;
    }
    
    setLoading(true);
    
    try {
      const selectedBusiness = businesses.find(b => String(b.id) === String(formData.business_id));
      const selectedService = services.find(s => String(s.id) === String(formData.service_id));
      const selectedStaff = staff.find(s => String(s.id) === String(formData.staff_id));

      const bookingData = {
        business_id: parseInt(formData.business_id),
        service_id: parseInt(formData.service_id),
        staff_id: formData.staff_id ? parseInt(formData.staff_id) : null,
        booking_date: formData.booking_date,
        start_time: formData.start_time,
        notes: formData.notes || null
      };
      
      console.log('Creating booking with data:', bookingData);
      
      const response = await bookingAPI.create(bookingData);
      console.log('Booking created:', response.data);
      
      toast.success('Booking created successfully!');
      
      // Navigate to booking confirmation with all details
      navigate('/booking-confirmation', { 
        state: { 
          booking: {
            id: response.data.data?.id,
            business_id: bookingData.business_id,
            business_name: selectedBusiness?.business_name,
            business_address: selectedBusiness?.address || selectedBusiness?.business_address,
            business_profile_image: selectedBusiness?.business_profile_image,
            service_id: bookingData.service_id,
            service_name: selectedService?.name,
            service_image: selectedService?.image || null,
            service_duration: selectedService?.duration_minutes,
            service_price: selectedService?.price,
            staff_id: bookingData.staff_id,
            staff_name: selectedStaff ? `${selectedStaff.first_name} ${selectedStaff.last_name}` : null,
            staff_profile_image: selectedStaff?.profile_image || null,
            booking_date: formData.booking_date,
            start_time: formData.start_time,
            end_time: selectedSlotDetails?.end_time || calculateEndTime(formData.start_time, selectedService?.duration_minutes),
            total_price: selectedService?.price,
            notes: formData.notes,
            status: 'pending'
          }
        } 
      });
    } catch (error) {
      console.error('Failed to create booking:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create booking. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    if (!startTime || !durationMinutes) return '';
    const [hoursRaw, minutesRaw] = String(startTime).split(':');
    const hours = Number(hoursRaw || 0);
    const minutes = Number(minutesRaw || 0);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';
    const totalMinutes = hours * 60 + minutes + Number(durationMinutes);
    const normalized = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
    const endHours = Math.floor(normalized / 60);
    const endMinutes = normalized % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  };

  const getSelectedBusinessName = () => {
    const business = businesses.find(b => String(b.id) === String(formData.business_id));
    return business?.business_name || '';
  };

  const getSelectedBusiness = () => {
    const business = businesses.find(b => String(b.id) === String(formData.business_id));
    return business || null;
  };

  const getSelectedServiceDetails = () => {
    const service = services.find(s => String(s.id) === String(formData.service_id));
    return service || null;
  };

  if (loadingBusinesses) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading businesses...</p>
        </div>
      </div>
    );
  }

  const selectedService = getSelectedServiceDetails();
  const selectedBusiness = getSelectedBusiness();
  const currencySymbol = selectedBusiness?.currency_symbol || '$';
  const selectedStaff = staff.find(s => String(s.id) === String(formData.staff_id)) || null;
  const anySlotAvailable = availableSlots.some(s => s.available != false && s.available != 0);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-2">Create New Booking</h1>
        <p className="text-gray-600 mb-6">Book an appointment with your favorite business</p>

        {selectedBusiness && (
          <div className="mb-6 p-4 border rounded-lg bg-gray-50 flex gap-4 items-center">
            {selectedBusiness.business_profile_image ? (
              <img
                src={resolveImageUrl(selectedBusiness.business_profile_image)}
                alt={selectedBusiness.business_name}
                className="h-14 w-14 rounded-xl object-cover border bg-white"
              />
            ) : (
              <div className="h-14 w-14 rounded-xl border bg-white flex items-center justify-center text-xl">
                🏪
              </div>
            )}
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate">{selectedBusiness.business_name}</div>
              <div className="text-sm text-gray-600 truncate">{selectedBusiness.address}</div>
            </div>
          </div>
        )}

        {selectedService && (
          <div className="mb-6 p-4 border rounded-lg bg-gray-50 flex gap-4 items-center">
            {selectedService.image && !serviceImageError ? (
              <img
                src={resolveImageUrl(selectedService.image)}
                alt={selectedService.name}
                className="h-14 w-14 rounded-xl object-cover border bg-white"
                onError={() => setServiceImageError(true)}
              />
            ) : (
              <div className="h-14 w-14 rounded-xl border bg-white flex items-center justify-center text-xl">
                ✂️
              </div>
            )}
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate">{selectedService.name}</div>
              <div className="text-sm text-gray-600 truncate">{selectedService.duration_minutes} min • {currencySymbol}{selectedService.price}</div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Business Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Select Business *</label>
            <select
              required
              value={formData.business_id}
              onChange={(e) => setFormData({ ...formData, business_id: e.target.value, service_id: '', staff_id: '', start_time: '' })}
              className="w-full input"
              disabled={loading}
            >
              <option value="">-- Choose a business --</option>
              {businesses.map(b => (
                <option key={b.id} value={b.id}>
                  {b.business_name} - {b.business_type}
                </option>
              ))}
            </select>
            {businesses.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">No businesses available. Please check back later.</p>
            )}
          </div>

          {/* Service Selection */}
          {formData.business_id && (
            <div>
              <label className="block text-sm font-medium mb-1">Select Service *</label>
              <select
                required
                value={formData.service_id}
                onChange={(e) => setFormData({ ...formData, service_id: e.target.value, start_time: '' })}
                className="w-full input"
                disabled={loading}
              >
                <option value="">-- Choose a service --</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} - {currencySymbol}{s.price} ({s.duration_minutes} min)
                  </option>
                ))}
              </select>
              {services.length === 0 && (
                <p className="text-sm text-amber-600 mt-1">No services available for this business.</p>
              )}
            </div>
          )}

          {/* Staff Selection (Optional) */}
          {formData.service_id && staff.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1">Select Staff (Optional)</label>
              <select
                value={formData.staff_id}
                onChange={(e) => {
                  setFormData({ ...formData, staff_id: e.target.value, start_time: '' });
                  setStaffAvatarError(false);
                }}
                className="w-full input"
                disabled={loading}
              >
                <option value="">Any staff member</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.first_name} {s.last_name} - {s.position}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedStaff && (
            <div className="p-4 border rounded-lg bg-gray-50 flex gap-4 items-center">
              {selectedStaff.profile_image && !staffAvatarError ? (
                <img
                  src={resolveImageUrl(selectedStaff.profile_image)}
                  alt={`${selectedStaff.first_name || ''} ${selectedStaff.last_name || ''}`.trim() || 'Staff'}
                  className="h-12 w-12 rounded-full object-cover border bg-white"
                  onError={() => setStaffAvatarError(true)}
                />
              ) : (
                <div className="h-12 w-12 rounded-full border bg-white flex items-center justify-center text-sm font-bold text-gray-500">
                  {(selectedStaff.first_name || 'S').slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 truncate">{selectedStaff.first_name} {selectedStaff.last_name}</div>
                <div className="text-sm text-gray-600 truncate">{selectedStaff.position}</div>
              </div>
            </div>
          )}

          {/* Date Selection */}
          {formData.service_id && (
            <div>
              <label className="block text-sm font-medium mb-1">Select Date *</label>
              <input
                type="date"
                required
                min={format(new Date(), 'yyyy-MM-dd')}
                value={formData.booking_date}
                onChange={(e) => setFormData({ ...formData, booking_date: e.target.value, start_time: '' })}
                className="w-full input"
                disabled={loading}
              />
            </div>
          )}

          {/* Time Slots - Improved Display */}
          {availableSlots.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1">Select Time Slot *</label>
              {!anySlotAvailable && (
                <div className="mb-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  {slotMessage || 'No available slots for this date. Please select another date.'}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-80 overflow-y-auto p-2 border rounded-lg">
                {availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      if (slot.available != false && slot.available != 0) {
                        handleSlotSelect(slot);
                      }
                    }}
                    disabled={slot.available == false || slot.available == 0}
                    className={`p-3 text-sm rounded-lg border-2 transition-all ${
                      slot.available == false || slot.available == 0
                        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                        : (formData.start_time === slot.start_time
                            ? 'bg-primary-600 text-white border-primary-600 shadow-md'
                            : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50')
                    }`}
                  >
                    <div className="font-medium">{slot.display_time}</div>
                    {slot.display_time && (
                      <div className="text-xs opacity-75 mt-1">
                        {selectedService?.duration_minutes} min
                      </div>
                    )}
                    {(slot.available == false || slot.available == 0) && (
                      <div className="text-xs mt-1">
                        {slot.unavailable_reason === 'past'
                          ? 'Past'
                          : (slot.unavailable_reason === 'booked' ? 'Booked' : 'Not available')}
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {selectedSlotDetails && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ Selected: {selectedSlotDetails.display_time}
                </p>
              )}
            </div>
          )}

          {formData.booking_date && formData.service_id && availableSlots.length === 0 && !loading && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-yellow-800">{slotMessage || 'No available slots for this date. Please select another date.'}</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1">Special Requests (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              className="w-full input"
              placeholder="Any special requests or additional information?"
              disabled={loading}
            />
          </div>

          {/* Booking Summary - Enhanced */}
          {formData.service_id && formData.start_time && (
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4 border border-primary-200">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Booking Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Business:</span>
                  <span className="font-medium">{getSelectedBusinessName()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-medium">{selectedService?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium">{selectedService?.duration_minutes} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">{formData.booking_date ? format(parseISO(formData.booking_date), 'EEEE, MMMM dd, yyyy') : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-medium text-primary-700">
                    {(selectedSlotDetails?.display_time || formData.start_time)}
                    {selectedService?.duration_minutes && ` (${selectedService.duration_minutes} min)`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Price:</span>
                  <span className="font-bold text-primary-600">{currencySymbol}{selectedService?.price}</span>
                </div>
                {formData.staff_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Staff:</span>
                    <span className="font-medium">{(() => {
                      const s = staff.find(s => String(s.id) === String(formData.staff_id));
                      return s ? `${s.first_name} ${s.last_name}` : '';
                    })()}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !formData.start_time || !formData.business_id || !formData.service_id || !formData.booking_date}
            className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Booking...
              </span>
            ) : (
              'Confirm Booking'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateBooking;
